import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const SECTION = 'cart-drawer-section';
let busy = false;

function money(cents) {
  const cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
  try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format(cents / 100); }
  catch (e) { return '$' + (cents / 100).toFixed(2); }
}
function rate() { return parseFloat((window.Shopify && Shopify.currency && Shopify.currency.rate) || '1') || 1; }

/** POST to a /cart endpoint and let the theme re-render the drawer through its standard event. */
async function cartOp(source, url, payload, action, lines) {
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'cart', lines, promise: deferred.promise }));
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...payload, sections: SECTION }),
    });
    const json = await res.json();
    const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, sections: json.sections, source: 'lv-cart-extras', didError: Boolean(json.status) },
    });
    return cart;
  } catch (err) {
    deferred.reject(err);
    throw err;
  }
}

/** Adds the free gift when the threshold is met, removes it when it is not. */
async function syncGift(source) {
  if (busy) return;
  const el = document.querySelector('lv-cart-extras');
  if (!el) return;
  const giftVariant = Number(el.dataset.giftVariant);
  if (!giftVariant) return;
  busy = true;
  try {
    const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
    const threshold = Number(el.dataset.threshold) * rate();
    const protVariant = Number(document.querySelector('[data-lv-prot]')?.dataset.lvProt || 0);
    let eligible = 0;
    let giftLine = null;
    for (const item of cart.items) {
      if (item.properties && item.properties._lv_gift === '1') giftLine = item;
      else if (item.variant_id !== protVariant) eligible += item.final_line_price;
    }
    if (eligible >= threshold && !giftLine) {
      await cartOp(source, '/cart/add.js', { items: [{ id: giftVariant, quantity: 1, properties: { _lv_gift: '1' } }] }, 'add', [{ merchandiseId: String(giftVariant), quantity: 1 }]);
    } else if (eligible < threshold && giftLine) {
      await cartOp(source, '/cart/change.js', { id: giftLine.key, quantity: 0 }, 'remove', [{ merchandiseId: String(giftLine.variant_id), quantity: 0 }]);
    } else if (giftLine && giftLine.quantity > 1) {
      await cartOp(source, '/cart/change.js', { id: giftLine.key, quantity: 1 }, 'update', [{ merchandiseId: String(giftLine.variant_id), quantity: 1 }]);
    }
  } catch (e) {
    console.warn('[lv-cart-extras]', e);
  } finally {
    busy = false;
  }
}

/* ---------- dispatch countdown (store time: America/New_York) ---------- */
function nyParts(d) {
  const f = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, weekday: 'short' });
  const o = {};
  for (const p of f.formatToParts(d)) o[p.type] = p.value;
  return o;
}
function updateTimer() {
  const el = document.querySelector('[data-lv-timer]');
  if (!el) return;
  const now = new Date();
  const p = nyParts(now);
  const secsLeft = 86400 - ((Number(p.hour) % 24) * 3600 + Number(p.minute) * 60 + Number(p.second));
  const h = Math.floor(secsLeft / 3600);
  const m = Math.floor((secsLeft % 3600) / 60);
  // dispatch date = 2 business days after today (same rule as the product page "Ships by")
  const base = new Date(Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day)));
  let added = 0;
  while (added < 2) { base.setUTCDate(base.getUTCDate() + 1); const wd = base.getUTCDay(); if (wd !== 0 && wd !== 6) added++; }
  const dateTxt = base.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
  el.querySelector('[data-lv-cd]').textContent = h + 'h ' + m + 'm';
  el.querySelector('[data-lv-date]').textContent = dateTxt;
  el.hidden = false;
}

class LvCartExtras extends HTMLElement {
  static observedAttributes = ['data-eligible', 'data-has-gift'];
  connectedCallback() { this.render(); updateTimer(); }
  attributeChangedCallback() { this.render(); }
  render() {
    updateTimer();
    const threshold = Number(this.dataset.threshold) * rate();
    const eligible = Number(this.dataset.eligible || 0);
    const name = this.dataset.giftName || 'gift';
    const txt = this.querySelector('[data-lv-goal-text]');
    const bar = this.querySelector('[data-lv-bar]');
    if (!txt || !bar) return;
    bar.style.width = Math.min(100, (eligible / threshold) * 100) + '%';
    if (rate() === 1) return;
    const left = Math.max(0, threshold - eligible);
    txt.innerHTML = left > 0
      ? `You're <strong>${money(left)}</strong> away from a <strong>FREE ${name}</strong>`
      : `🎉 You've unlocked a <strong>FREE ${name}</strong>`;
  }
}
if (!customElements.get('lv-cart-extras')) customElements.define('lv-cart-extras', LvCartExtras);

if (!window.__lvCartExtras) {
  window.__lvCartExtras = true;
  setInterval(updateTimer, 30000);

  document.addEventListener('click', async (e) => {
    const btn = e.target.closest && e.target.closest('[data-lv-add]');
    if (!btn) return;
    e.preventDefault();
    btn.setAttribute('aria-busy', 'true');
    const id = Number(btn.dataset.lvAdd);
    try {
      await cartOp(btn, '/cart/add.js', { items: [{ id, quantity: 1 }] }, 'add', [{ merchandiseId: String(id), quantity: 1 }]);
      await syncGift(btn);
    } finally { btn.removeAttribute('aria-busy'); }
  });

  document.addEventListener('change', async (e) => {
    const box = e.target.closest && e.target.closest('[data-lv-prot]');
    if (!box) return;
    const id = Number(box.dataset.lvProt);
    box.disabled = true;
    try {
      if (box.checked) {
        await cartOp(box, '/cart/add.js', { items: [{ id, quantity: 1 }] }, 'add', [{ merchandiseId: String(id), quantity: 1 }]);
      } else {
        const cart = await (await fetch('/cart.js')).json();
        const line = cart.items.find((i) => i.variant_id === id);
        if (line) await cartOp(box, '/cart/change.js', { id: line.key, quantity: 0 }, 'remove', [{ merchandiseId: String(id), quantity: 0 }]);
      }
    } finally { box.disabled = false; }
  });

  // Re-check the gift after any cart change made elsewhere (product form, quantity, Kaching bundles...)
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    event.promise?.then(({ detail }) => {
      setTimeout(() => document.querySelectorAll('lv-cart-extras').forEach((el) => el.render()), 150);
      if (detail?.source === 'lv-cart-extras') return;
      setTimeout(() => syncGift(document.body), 50);
    }).catch(() => {});
  });

  syncGift(document.body);
}
